import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { RestProviderService } from 'src/app/services/rest-provider/rest-provider.service';
import { environment } from 'src/environments/environment';

/** Mirrors Semantika.WebGalis.Core.Application.Bulk.BulkColumnDef. */
export interface BulkColumnDef {
    Binding: string;
    Title: string;
    ControlName: string;
    /** 0 Text | 1 Number | 2 Decimal | 3 Date | 4 Checkbox | 5 Registry | 6 Collection | 7 Unsupported */
    Kind: number;
    /** Kind 7 only: why this column cannot be imported. Shown next to the greyed-out column. */
    UnsupportedReason?: string;
    // Kind 6 (Collection): one cell holds several values, split on SplitterChars.
    SplitterChars?: string;
    IsMultiValue?: boolean;
    ChildTitle?: string;
    Required: boolean;
    ReadOnly: boolean;
    RegistryModelFullName: string;
    RegistryTypeName: string;
    InDefaultLayout: boolean;
    IsDocumentReference: boolean;
    MaxLength: number;
}

export interface BulkColumnsResponse {
    ModelFullName: string;
    Mode: string;
    KeyProperty: string;
    IsDocument: boolean;
    AppliedLayout: string;
    MissingRequestedBindings: string[];
    FormLink: string;
    Columns: BulkColumnDef[];
}

export interface BulkParseResponse {
    Headers: string[];
    SuggestedMapping: { [header: string]: string };
    Rows: { [binding: string]: string }[];
    RowCount: number;
    IgnoredHeaders: string[];
    /** The uploaded file name, echoed back by the server so commit can report it. */
    SourceFileName: string;
    /** Correlation id for this run, minted server-side at parse. Sent back at commit. */
    ImportId: string;
    /**
     * Parallel to Rows: the physical 1-based sheet row each parsed row came from. NOT derivable
     * here - Rows is compacted server-side (header consumed, null and all-empty rows skipped),
     * so an index into it is not a row of the workbook.
     */
    SheetRowNumbers: number[];
}

export interface BulkRowMessage {
    RowIndex: number;
    Binding: string;
    Message: string;
    IsError: boolean;
}

export interface BulkValidateResponse {
    IsValid: boolean;
    Messages: BulkRowMessage[];
    ResolvedLookups: { [binding: string]: number }[];
}

export interface BulkCommitRowResult {
    RowIndex: number;
    Success: boolean;
    CreatedId: number | null;
    Error: string;
}

/**
 * One selectable import target. Deliberately NOT the metamodel entity: that payload is ~950 KB
 * and carries the full NHibernate mapping per entity. This is what the picker needs and nothing
 * more, projected down at the moment the responses land.
 */
export interface BulkModelChoice {
    fullName: string;
    /** CLR type name, e.g. ItemModel. */
    name: string;
    /** What the curator reads. Translation when the server has one, otherwise a de-suffixed name. */
    label: string;
    type: 'document' | 'registry';
    /** Second-to-last namespace segment, e.g. Collections. Used to group the list. */
    section: string;
    /** From the metamodel join. Empty until that response lands, or if it fails. */
    moduleName: string;
}

export interface BulkCommitResponse {
    Created: number;
    Failed: number;
    Results: BulkCommitRowResult[];
}

/**
 * Five-step bulk-import wizard at /cms/bulk-import.
 *
 * Step 0 picks the import target. `?model=<FullName>` on the route preselects it and lands the
 * user straight on the file step, which is what the navigation entries in
 * Application/Bulk/Sql/register-navigation.sql use. A bare /cms/bulk-import with no model still
 * lands on the picker.
 */
@Component({
    selector: 'app-bulk-import',
    templateUrl: './bulk-import.component.html',
    styleUrls: ['./bulk-import.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule]
})
export class BulkImportComponent implements OnInit {

    /**
     * The import target. Was a constant pinned to ItemModel; it is state now. Every one of the
     * five endpoints already took it as a parameter, so nothing on the backend moved.
     */
    modelFullName: string = null;

    /** What the picker chose, kept for the labels on later steps. */
    selectedModel: BulkModelChoice = null;

    /** 0 Izbira modela | 1 Datoteka | 2 Preslikava | 3 Predogled | 4 Uvoz */
    step = 0;

    // ------------------------------------------------- step 0: the model picker
    //
    // The list is NOT discovered by a new endpoint. `POST /api/user/allowedsearchclass` is
    // already PERMISSION-SCOPED to the current user, so a curator is only ever offered models
    // they are allowed to touch; the metamodel is joined in only for the module name. Same shape
    // as ngWgPlatform's ModelExplorerService.getModules.

    modelChoices: BulkModelChoice[] = [];
    modelsLoading = false;
    modelSearch = '';
    /** '' = both. */
    modelTypeFilter: '' | 'document' | 'registry' = 'document';
    /** Recomputed on change, never a getter — 91 choices scanned per change-detection cycle. */
    modelGroups: { section: string; items: BulkModelChoice[] }[] = [];
    /** True once a `?model=` param was honoured, so step 1 can offer "change the model". */
    modelFromRoute = false;
    busy = false;
    error: string = null;

    columns: BulkColumnDef[] = [];

    /**
     * Kind 7. Returned by the API on purpose and kept OUT of `columns`, so nothing downstream —
     * mapping dropdown, grid, commit payload — can ever reach one. They are listed separately with
     * their reason: a curator who cannot find a column they expect must be able to see WHY it is
     * absent, instead of it being dropped invisibly.
     */
    unsupportedColumns: BulkColumnDef[] = [];
    columnsResponse: BulkColumnsResponse = null;

    file: File = null;
    fileName: string = null;

    parsed: BulkParseResponse = null;
    /** header -> binding ('' == ignore). Seeded from SuggestedMapping, user-adjustable. */
    mapping: { [header: string]: string } = {};

    rows: { [binding: string]: string }[] = [];
    validation: BulkValidateResponse = null;
    commitResult: BulkCommitResponse = null;

    /** binding -> option names, for the three TRUE registries only. */
    registryOptions: { [binding: string]: string[] } = {};

    // ------------------------------------------------- Kind 6 (Collection) pill editor
    //
    // A collection cell STAYS a delimited string in `rows` - the same string commit() posts and the
    // template round-trips. Pills are a VIEW over it; every add/remove rewrites the string. Nothing
    // about the commit payload, the validation contract or the template changes.

    /** Rows this component unticked because they had an error, so it can tick them back. */
    private autoUnselected: boolean[] = [];

    /** binding -> suggestion names for a Kind 6 target registry. Loaded LAZILY, on first open. */
    collectionOptions: { [binding: string]: string[] } = {};
    private collectionFeedState: { [binding: string]: 'loading' | 'ready' | 'failed' } = {};

    /**
     * The open picker, or null. `tokenIndex` -1 means "append", otherwise "replace this token".
     * Rendered inline under the cell rather than as an overlay: the list page's sticky header
     * intercepts absolutely-positioned popups, and an inline panel cannot lose that fight.
     */
    picker: {
        rowIndex: number;
        col: BulkColumnDef;
        tokenIndex: number;
        text: string;
        note: string;
    } = null;

    /** `${rowIndex}|${binding}` -> messages for that cell. */
    private cellMessages: { [key: string]: BulkRowMessage[] } = {};
    /** rowIndex -> messages not tied to a known column. */
    rowLevelMessages: { [rowIndex: number]: BulkRowMessage[] } = {};

    // ------------------------------------------------- grid: selection / paging / filter
    //
    // Every one of these is keyed on the ORIGINAL row index in `rows`. Nothing here is ever
    // page-local: a selection count that disagrees with what actually commits is worse than
    // no count at all.

    /** Parallel to `rows`. What the user has ticked for import. */
    selected: boolean[] = [];
    /** Parallel to `rows`. Built once per validation pass instead of rescanning Messages per row. */
    rowHasErr: boolean[] = [];
    rowHasWarn: boolean[] = [];

    /** Whole-sheet filter, not a page filter. */
    errorsOnly = false;

    pageSize = 50;
    pageIndex = 0;

    /** Original indices surviving the filter, whole sheet. */
    filteredIndices: number[] = [];
    /** The slice of `filteredIndices` currently rendered. */
    visibleIndices: number[] = [];

    /** Original indices, in the order they were POSTed, so the step-4 report can name real rows. */
    private committedIndices: number[] = [];

    // ------------------------------------------------- grid: which COLUMNS are rendered
    //
    // The grid used to render every mappable column in the model — 73 for ItemModel after the
    // widening pass, which is unreadable when the sheet carried eight. Default is now the columns
    // the sheet actually mapped; ticking `showAllColumns` adds the rest so a curator can fill in a
    // location or a collection for every row before committing.
    //
    // NOTE: this narrows the VIEW only. `rows` still carries every binding, validation still runs
    // over the whole row, and every pill below still counts the whole sheet.

    /** Bindings the mapping step actually claimed. Captured when the preview is built. */
    mappedBindings: string[] = [];

    /** Ticked = show the remaining mappable columns too. Survives step navigation on purpose. */
    showAllColumns = false;

    /** What the grid renders. A FIELD, not a getter: a getter here is re-evaluated per row per
     *  change-detection cycle, which is invisible at 4 rows and quadratic at 250. */
    gridColumns: BulkColumnDef[] = [];

    /** Bindings carrying at least one validation message, whole sheet. Always rendered, mapped or
     *  not — a hidden column with an error turns the row red with no visible reason. */
    private messageBindings = new Set<string>();

    constructor(
        private http: HttpClient,
        private rest: RestProviderService,
        private route: ActivatedRoute) { }

    ngOnInit(): void {
        // A model on the route skips the picker. Anything else — absent, blank, or a name the
        // column generator refuses — lands on the picker, which is the fallback demo path and
        // must never depend on the navigation rows existing.
        const fromRoute = (this.route.snapshot.queryParamMap.get('model')
            || this.route.snapshot.paramMap.get('model') || '').trim();

        if (fromRoute) {
            this.modelFullName = fromRoute;
            this.modelFromRoute = true;
            this.step = 1;
            this.loadColumns();
        }

        // Loaded either way: the picker needs it, and step 1 uses it to name the target of a
        // model that arrived over the route.
        this.loadModelChoices();
    }

    // ---------------------------------------------------------------- step 0: model picker

    loadModelChoices(): void {
        this.modelsLoading = true;
        const opts = { headers: this.headers() };

        forkJoin({
            documents: this.http.post<any[]>(
                this.url('/api/user/allowedsearchclass'), { globalSearchType: 'document' }, opts),
            registries: this.http.post<any[]>(
                this.url('/api/user/allowedsearchclass'), { globalSearchType: 'registry' }, opts)
        }).subscribe({
            next: (r) => {
                this.modelChoices = [
                    ...(r.documents || []).map(c => this.toChoice(c, 'document')),
                    ...(r.registries || []).map(c => this.toChoice(c, 'registry'))
                ].sort((a, b) => a.label.localeCompare(b.label, 'sl'));
                this.modelsLoading = false;
                this.rebuildModelGroups();
                this.adoptRouteModelLabel();
                this.loadModuleNames();
            },
            error: (err) => { this.modelsLoading = false; this.fail('Seznam modelov', err); }
        });
    }

    /**
     * The module name comes from `POST /api/metamodel/entities`, which answers ~950 KB of full
     * NHibernate mapping detail. It is fired SEPARATELY from the two calls above and its result is
     * projected to a FullName -> ModuleName map on arrival; the entity array is never held. The
     * picker renders without it and simply carries no module chip if it fails.
     */
    private loadModuleNames(): void {
        this.http.post<any[]>(this.url('/api/metamodel/entities'), {}, { headers: this.headers() })
            .subscribe({
                next: (entities) => {
                    const byName: { [full: string]: string } = {};
                    for (const e of entities || []) {
                        if (e && e.FullName && e.ModuleName) { byName[e.FullName] = e.ModuleName; }
                    }
                    for (const c of this.modelChoices) { c.moduleName = byName[c.fullName] || ''; }
                },
                error: () => { /* module chips are decoration; the picker stands without them */ }
            });
    }

    private toChoice(c: any, type: 'document' | 'registry'): BulkModelChoice {
        const name: string = c.Name || '';
        const translation: string = c.Translation || '';
        // Registries come back with Translation === Name on this database, so a raw Translation
        // would show "AcquisitionMethodModel" as if it were a label. De-suffix in that case.
        const label = (translation && translation !== name)
            ? translation
            : name.replace(/Model$/, '');
        return {
            fullName: c.FullName,
            name: name,
            label: label || c.FullName,
            type: type,
            section: this.sectionOf(c.FullName),
            moduleName: ''
        };
    }

    private sectionOf(fullName: string): string {
        const parts = (fullName || '').split('.');
        return parts.length >= 2 ? parts[parts.length - 2] : 'Ostalo';
    }

    /** Name the target when the model arrived over the route rather than through the picker. */
    private adoptRouteModelLabel(): void {
        if (!this.modelFullName || this.selectedModel) { return; }
        this.selectedModel = this.modelChoices.find(c => c.fullName === this.modelFullName) || null;
    }

    onModelSearchChanged(): void { this.rebuildModelGroups(); }

    setModelTypeFilter(t: '' | 'document' | 'registry'): void {
        this.modelTypeFilter = t;
        this.rebuildModelGroups();
    }

    private rebuildModelGroups(): void {
        const q = (this.modelSearch || '').trim().toLowerCase();
        const matches = this.modelChoices.filter(c => {
            if (this.modelTypeFilter && c.type !== this.modelTypeFilter) { return false; }
            if (!q) { return true; }
            return c.label.toLowerCase().indexOf(q) >= 0
                || c.name.toLowerCase().indexOf(q) >= 0
                || c.section.toLowerCase().indexOf(q) >= 0;
        });

        const bySection: { [s: string]: BulkModelChoice[] } = {};
        for (const c of matches) { (bySection[c.section] = bySection[c.section] || []).push(c); }
        this.modelGroups = Object.keys(bySection)
            .sort((a, b) => a.localeCompare(b, 'sl'))
            .map(s => ({ section: s, items: bySection[s] }));
    }

    get modelMatchCount(): number {
        return this.modelGroups.reduce((n, g) => n + g.items.length, 0);
    }

    /** Picking a model resets everything downstream — a sheet parsed against the old one is
     *  meaningless against the new one, and leaving it in place is how a curator commits rows
     *  into the wrong entity. */
    chooseModel(c: BulkModelChoice): void {
        this.restart();
        this.modelFullName = c.fullName;
        this.selectedModel = c;
        this.modelFromRoute = false;
        this.columns = [];
        this.unsupportedColumns = [];
        this.columnsResponse = null;
        this.registryOptions = {};
        this.collectionOptions = {};
        this.collectionFeedState = {};
        this.picker = null;
        this.step = 1;
        this.loadColumns();
    }

    /** Back to the picker from step 1. */
    changeModel(): void {
        this.error = null;
        this.step = 0;
    }

    get modelLabel(): string {
        if (this.selectedModel) { return this.selectedModel.label; }
        if (!this.modelFullName) { return ''; }
        const parts = this.modelFullName.split('.');
        return parts[parts.length - 1].replace(/Model$/, '');
    }

    // ---------------------------------------------------------------- plumbing

    private headers(): HttpHeaders {
        return new HttpHeaders({
            'Authorization': 'Bearer ' + this.rest.getToken(),
            'Accept-Language': environment.language || 'sl'
        });
    }

    private url(path: string): string {
        return (environment.wgProfileHost || '') + path;
    }

    private fail(context: string, err: any): void {
        this.busy = false;
        const detail = err && (err.error && typeof err.error === 'string' ? err.error : err.message);
        this.error = context + ': ' + (detail || 'neznana napaka');
        // Surfaced in the UI, and left on the console so a Playwright pageerror run names it.
        console.warn('[bulk-import] ' + this.error);
    }

    // ---------------------------------------------------------------- step 0: columns

    loadColumns(): void {
        if (!this.modelFullName) { this.step = 0; return; }
        this.busy = true;
        this.error = null;
        this.http.get<BulkColumnsResponse>(
            this.url('/api/bulk/columns'),
            { headers: this.headers(), params: { model: this.modelFullName, mode: 'add' } }
        ).subscribe({
            next: (r) => {
                this.columnsResponse = r;
                const all = r.Columns || [];
                this.columns = all.filter(c => c.Kind !== 7);
                this.unsupportedColumns = all.filter(c => c.Kind === 7);
                this.busy = false;
                this.loadRegistryOptions();
            },
            error: (e) => {
                // A model the column generator refuses leaves step 1 with nothing to do, so send
                // the user back to the picker rather than stranding them on a dead file input.
                this.fail('Branje stolpcev ni uspelo za „' + this.modelLabel + '“', e);
                this.columns = [];
                this.unsupportedColumns = [];
                this.columnsResponse = null;
                this.step = 0;
            }
        });
    }

    /**
     * Only for columns where IsDocumentReference is false. api/registry/{type}/dropdown 500s for a
     * document reference (ItemModel.Location is a GalisDocument, not an IRegistry) — those stay a
     * plain text cell and are resolved by name on the server. See RESULT.md, C1.
     */
    private loadRegistryOptions(): void {
        this.columns
            .filter(c => c.Kind === 5 && !c.IsDocumentReference && c.RegistryTypeName)
            .forEach(c => {
                this.http.get<any[]>(
                    this.url('/api/registry/' + c.RegistryTypeName + '/dropdown'),
                    { headers: this.headers() }
                ).subscribe({
                    next: (opts) => {
                        this.registryOptions[c.Binding] = (opts || [])
                            .map(o => o.Name || o.Title)
                            .filter(n => !!n);
                    },
                    error: () => { this.registryOptions[c.Binding] = []; }
                });
            });
    }

    // ---------------------------------------------------------------- step 1: template

    downloadingTemplate = false;

    /**
     * Fetch the starter .xlsx. It goes through HttpClient rather than a plain <a href> because the
     * endpoint is Bearer-authenticated - a naked link would arrive unauthenticated and 401.
     */
    downloadTemplate(): void {
        this.downloadingTemplate = true;
        this.error = null;
        this.http.get(this.url('/api/bulk/template'), {
            headers: this.headers(),
            params: { model: this.modelFullName, mode: 'add' },
            responseType: 'blob',
            observe: 'response'
        }).subscribe({
            next: (resp) => {
                this.downloadingTemplate = false;
                const blob = resp.body;
                if (!blob) { this.error = 'Predloga je prazna.'; return; }

                // Prefer the server's filename; it carries the model and the date.
                let name = 'predloga-uvoz.xlsx';
                const cd = resp.headers.get('Content-Disposition');
                if (cd) {
                    const m = /filename\*?=(?:UTF-8'')?\"?([^\";]+)/i.exec(cd);
                    if (m && m[1]) name = decodeURIComponent(m[1].trim());
                }

                const href = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = href;
                a.download = name;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(href);
            },
            error: (e) => {
                this.downloadingTemplate = false;
                this.fail('Prenos predloge ni uspel', e);
            }
        });
    }

    // ---------------------------------------------------------------- step 1: upload

    onFileSelected(ev: any): void {
        const f = ev && ev.target && ev.target.files ? ev.target.files[0] : null;
        this.file = f || null;
        this.fileName = f ? f.name : null;
        this.error = null;
    }

    /** Step 1 -> 2. Parse with no mapping so the server returns its SuggestedMapping. */
    parseFile(): void {
        if (!this.file) { this.error = 'Izberite .xlsx datoteko.'; return; }
        this.busy = true;
        this.error = null;

        const fd = new FormData();
        fd.append('file', this.file, this.file.name);
        fd.append('modelFullName', this.modelFullName);

        this.http.post<BulkParseResponse>(this.url('/api/bulk/parse'), fd, { headers: this.headers() })
            .subscribe({
                next: (r) => {
                    this.parsed = r;
                    this.mapping = {};
                    (r.Headers || []).forEach(h => {
                        this.mapping[h] = (r.SuggestedMapping && r.SuggestedMapping[h]) || '';
                    });
                    this.busy = false;
                    this.step = 2;
                },
                error: (e) => this.fail('Branje datoteke ni uspelo', e)
            });
    }

    // ---------------------------------------------------------------- step 2: mapping

    /**
     * DISTINCT header strings, first-occurrence order. `mapping` is keyed on the header STRING,
     * so a sheet with e.g. "Naslov" in two columns can only ever hold one mapping entry for it —
     * rendering one row per raw header would show two identical, invisibly-linked dropdowns and
     * silently drop the second column's data. See `duplicateHeaderNames` for the loud warning.
     */
    get mappedHeaders(): string[] {
        if (!this.parsed) return [];
        const seen = new Set<string>();
        const out: string[] = [];
        (this.parsed.Headers || []).forEach(h => {
            if (h && !seen.has(h)) { seen.add(h); out.push(h); }
        });
        return out;
    }

    /**
     * Header strings that occur more than once in the raw sheet. Only the first occurrence
     * survives into `mapping` — every later column with the same header is dropped before
     * mapping even starts. Must be surfaced; the mapped-count must never look "complete" while
     * this is non-empty.
     */
    get duplicateHeaderNames(): string[] {
        if (!this.parsed) return [];
        const counts: { [h: string]: number } = {};
        (this.parsed.Headers || []).forEach(h => { if (h) { counts[h] = (counts[h] || 0) + 1; } });
        return Object.keys(counts).filter(h => counts[h] > 1);
    }

    /** This header string occurs more than once in the raw sheet (see `duplicateHeaderNames`). */
    isDuplicateHeaderName(header: string): boolean {
        return this.duplicateHeaderNames.indexOf(header) !== -1;
    }

    /** A binding already claimed by a DIFFERENT header — the server keeps only the first. */
    isDuplicateBinding(header: string): boolean {
        const b = this.mapping[header];
        if (!b) return false;
        return Object.keys(this.mapping).filter(h => this.mapping[h] === b).length > 1;
    }

    get mappingHasDuplicates(): boolean {
        return this.mappedHeaders.some(h => this.isDuplicateBinding(h)) || this.duplicateHeaderNames.length > 0;
    }

    get mappedCount(): number {
        return this.mappedHeaders.filter(h => !!this.mapping[h]).length;
    }

    /** Step 2 -> 3. Re-parse WITH the mapping; the server re-keys every row by binding. */
    applyMappingAndPreview(): void {
        if (!this.file) { this.error = 'Datoteka ni več na voljo — začnite znova.'; return; }
        this.busy = true;
        this.error = null;

        const explicit: { [h: string]: string } = {};
        Object.keys(this.mapping).forEach(h => { if (this.mapping[h]) explicit[h] = this.mapping[h]; });

        const fd = new FormData();
        fd.append('file', this.file, this.file.name);
        fd.append('modelFullName', this.modelFullName);
        fd.append('mapping', JSON.stringify(explicit));

        this.http.post<BulkParseResponse>(this.url('/api/bulk/parse'), fd, { headers: this.headers() })
            .subscribe({
                next: (r) => {
                    this.parsed = r;
                    this.rows = (r.Rows || []).map(row => {
                        const copy: { [k: string]: string } = {};
                        this.columns.forEach(c => { copy[c.Binding] = row[c.Binding] != null ? row[c.Binding] : ''; });
                        return copy;
                    });
                    // Everything is ticked to begin with; validation then unticks what it must.
                    // The bindings this sheet actually claimed, in model order, deduped.
                    const claimed = new Set<string>();
                    Object.keys(this.mapping).forEach(h => { if (this.mapping[h]) claimed.add(this.mapping[h]); });
                    this.mappedBindings = this.columns.map(c => c.Binding).filter(b => claimed.has(b));
                    this.messageBindings = new Set<string>();
                    this.recomputeGridColumns();

                    this.selected = this.rows.map(() => true);
                    this.autoUnselected = this.rows.map(() => false);
                    this.rowHasErr = this.rows.map(() => false);
                    this.rowHasWarn = this.rows.map(() => false);
                    this.errorsOnly = false;
                    this.pageIndex = 0;
                    this.recomputeVisible();
                    this.busy = false;
                    this.step = 3;
                    this.validate();
                },
                error: (e) => this.fail('Ponovno branje z izbrano preslikavo ni uspelo', e)
            });
    }

    // ---------------------------------------------------------------- step 3: preview + validate

    validate(): void {
        if (!this.rows.length) { this.error = 'Datoteka ne vsebuje vrstic.'; return; }
        this.busy = true;
        this.error = null;

        this.http.post<BulkValidateResponse>(
            this.url('/api/bulk/validate'),
            { ModelFullName: this.modelFullName, Mode: 'add', Rows: this.rows },
            { headers: this.headers() }
        ).subscribe({
            next: (r) => {
                this.validation = r;
                this.indexMessages(r);
                this.busy = false;
            },
            error: (e) => this.fail('Preverjanje ni uspelo', e)
        });
    }

    private indexMessages(r: BulkValidateResponse): void {
        this.cellMessages = {};
        this.rowLevelMessages = {};
        this.rowHasErr = this.rows.map(() => false);
        this.rowHasWarn = this.rows.map(() => false);

        const known = new Set(this.columns.map(c => c.Binding));
        this.messageBindings = new Set<string>();
        (r.Messages || []).forEach(m => {
            if (m.Binding && known.has(m.Binding)) {
                const k = m.RowIndex + '|' + m.Binding;
                (this.cellMessages[k] = this.cellMessages[k] || []).push(m);
                this.messageBindings.add(m.Binding);
            } else {
                (this.rowLevelMessages[m.RowIndex] = this.rowLevelMessages[m.RowIndex] || []).push(m);
            }
            if (m.RowIndex >= 0 && m.RowIndex < this.rows.length) {
                if (m.IsError) { this.rowHasErr[m.RowIndex] = true; }
                else { this.rowHasWarn[m.RowIndex] = true; }
            }
        });

        // A row that has just GAINED an error must lose its tick. Leaving it ticked would let the
        // selection count promise a row the commit path will refuse to send.
        //
        // And the other half, which the pills made load-bearing: a row the SYSTEM unticked and that
        // is now clean must get its tick BACK. Without it, correcting a bad token in the grid leaves
        // the row silently out of the import - the counters read "2 brez napak / 0 vrstic z napako"
        // while only one row commits, and nothing points the curator at the checkbox. Measured on
        // the rig before this existed: the row whose author was corrected by hand was precisely the
        // one that never reached galis.item.
        //
        // A row the USER unticked stays unticked - `autoUnselected` is set only here and cleared the
        // moment the user touches that checkbox.
        this.rows.forEach((_, i) => {
            if (this.rowHasErr[i]) {
                if (this.selected[i]) { this.autoUnselected[i] = true; }
                this.selected[i] = false;
            } else if (this.autoUnselected[i]) {
                this.selected[i] = true;
                this.autoUnselected[i] = false;
            }
        });
        this.recomputeGridColumns();
        this.recomputeVisible();
    }

    // ---------------------------------------------------------------- Kind 6: pills

    /**
     * The desktop's own rule, ported verbatim: split on SplitterChars, trim, drop empties, then
     * .Distinct case-insensitively - SimpleBulkType.cs:1785-1787 + :1876-1880, splitters
     * BulkContainer.cs:41 `{ ',', ';' }`. First occurrence wins and token order is preserved, which
     * is what Distinct() does in .NET too.
     */
    tokensOf(value: string, col: BulkColumnDef): string[] {
        if (!value) return [];
        const splitters = (col && col.SplitterChars) ? col.SplitterChars : ',;';
        const out: string[] = [];
        const seen = new Set<string>();
        value.split(new RegExp('[' + splitters.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&') + ']'))
            .map(t => t.trim())
            .filter(t => t.length > 0)
            .forEach(t => {
                const k = t.toLocaleLowerCase();
                if (seen.has(k)) return;
                seen.add(k);
                out.push(t);
            });
        return out;
    }

    /**
     * The desktop never re-joins - its cell value IS the bound list and the displayed string is
     * cosmetic, hard-coded ", " with a trailing separator (BulkDataGridAddRemoveSifrant.cs:17-29).
     * Here the string is the truth, so we own the join: ", ", no trailing separator. It re-splits
     * under the same rule on both client and server.
     */
    private joinTokens(tokens: string[]): string {
        return tokens.join(', ');
    }

    /**
     * Pill state per TOKEN.
     *
     * It cannot come from ResolvedLookups: that is `{ binding -> one id }` and the Collection branch
     * of Validate (BulkService.cs:941-964) never writes to it - it only emits one message per
     * unresolved token. So the messages ARE the per-token record, and the server names the token in
     * quotes on purpose:
     *     "{label} '{token}' ne obstaja."
     *     "Vrednost \"{token}\" v sifrantu \"{label}\" ni enolicna ..."
     *
     * 'unknown' (no validation yet, or the sheet was edited since) renders neutral. Claiming a pill
     * is bound when nothing has verified it would be the same lie as a green badge over an unchecked
     * chain.
     */
    pillsFor(rowIndex: number, col: BulkColumnDef): { t: string; s: string }[] {
        const tokens = this.tokensOf(this.rows[rowIndex] ? this.rows[rowIndex][col.Binding] : '', col);
        if (!this.validation) return tokens.map(t => ({ t: t, s: 'unknown' }));
        const msgs = this.messagesFor(rowIndex, col.Binding).filter(m => m.IsError);
        return tokens.map(t => ({ t: t, s: this.tokenIsFlagged(msgs, t) ? 'bad' : 'ok' }));
    }

    private tokenIsFlagged(msgs: BulkRowMessage[], token: string): boolean {
        const a = "'" + token + "'";
        const b = '"' + token + '"';
        return msgs.some(m => !!m.Message && (m.Message.indexOf(a) >= 0 || m.Message.indexOf(b) >= 0));
    }

    /** The server's own words for one bad token, so the pill's tooltip says why. */
    pillMessage(rowIndex: number, col: BulkColumnDef, token: string): string {
        const a = "'" + token + "'";
        const b = '"' + token + '"';
        const m = this.messagesFor(rowIndex, col.Binding)
            .find(x => x.IsError && !!x.Message && (x.Message.indexOf(a) >= 0 || x.Message.indexOf(b) >= 0));
        return m ? m.Message : ('Kliknite za urejanje: ' + token);
    }

    private writeTokens(rowIndex: number, col: BulkColumnDef, tokens: string[]): void {
        this.rows[rowIndex][col.Binding] = this.joinTokens(tokens);
        this.onCellEdited();
    }

    removeToken(rowIndex: number, col: BulkColumnDef, tokenIndex: number): void {
        const tokens = this.tokensOf(this.rows[rowIndex][col.Binding], col);
        if (tokenIndex < 0 || tokenIndex >= tokens.length) return;
        tokens.splice(tokenIndex, 1);
        this.closePicker();
        this.writeTokens(rowIndex, col, tokens);
    }

    isPickerOpen(rowIndex: number, binding: string): boolean {
        return !!this.picker && this.picker.rowIndex === rowIndex && this.picker.col.Binding === binding;
    }

    /** tokenIndex -1 = add a new token; otherwise edit the one at that position. */
    openPicker(rowIndex: number, col: BulkColumnDef, tokenIndex: number): void {
        const tokens = this.tokensOf(this.rows[rowIndex][col.Binding], col);
        this.picker = {
            rowIndex: rowIndex,
            col: col,
            tokenIndex: tokenIndex,
            text: tokenIndex >= 0 ? (tokens[tokenIndex] || '') : '',
            note: null
        };
        this.ensureCollectionFeed(col);
    }

    closePicker(): void { this.picker = null; }

    applyPicker(): void {
        if (!this.picker) return;
        const p = this.picker;
        const raw = (p.text || '').trim();
        const tokens = this.tokensOf(this.rows[p.rowIndex][p.col.Binding], p.col);

        if (!raw) {
            // An emptied edit box removes the token; an empty add is just a cancel.
            if (p.tokenIndex >= 0) { this.removeToken(p.rowIndex, p.col, p.tokenIndex); return; }
            this.closePicker();
            return;
        }

        // Case-insensitive duplicate, desktop rule. The desktop drops these SILENTLY
        // (DataGridAddRemoveSifrant.cs:188-201); saying so costs one line and stops the curator
        // wondering why their click did nothing.
        const dupAt = tokens.findIndex(t => t.toLocaleLowerCase() === raw.toLocaleLowerCase());
        if (dupAt >= 0 && dupAt !== p.tokenIndex) {
            p.note = '"' + raw + '" je na seznamu ze.';
            return;
        }

        if (p.tokenIndex >= 0) tokens[p.tokenIndex] = raw; else tokens.push(raw);
        this.closePicker();
        this.writeTokens(p.rowIndex, p.col, tokens);
    }

    /**
     * Suggestions for one Kind 6 target, fetched at most once per column and only when a picker is
     * first opened for it. Seven collection columns on ItemModel; loading them all up front would
     * cost seven ~100 KB calls before the curator has clicked anything.
     *
     * Two things this list is NOT.
     *
     * It is not a closed set. RegistryController.cs:85 defaults `take = 1000`, and this database
     * holds 6390 authors and 2386 classifications - so the list is TRUNCATED for three of the seven
     * targets, and `take=7000` does not return inside a minute. A name past the cap must still be
     * typeable, and the server (not this list) decides whether it resolves.
     *
     * It is not searched server-side. `?q=` answers HTTP 500 on this route; the product's own
     * sem-multiple-select carries the same note at :36. Filtering is client-side.
     */
    private ensureCollectionFeed(col: BulkColumnDef): void {
        if (!col || this.collectionFeedState[col.Binding]) return;

        // The trap the brief names: /dropdown 500s for a document reference. Kind 6 targets on
        // ItemModel are all registries today, but TryBuildCollectionColumn computes the flag
        // honestly, so honour it rather than assume. Degrades to free-text entry.
        if (col.IsDocumentReference || !col.RegistryTypeName) {
            this.collectionFeedState[col.Binding] = 'failed';
            this.collectionOptions[col.Binding] = [];
            return;
        }

        this.collectionFeedState[col.Binding] = 'loading';
        this.http.get<any[]>(
            this.url('/api/registry/' + col.RegistryTypeName + '/dropdown'),
            { headers: this.headers() }
        ).subscribe({
            next: (opts) => {
                // Title, NOT Name. The author feed returns { Name: "BOGDAN", Title: "BOGDAN BORCIC" } -
                // Name is the FIRST NAME, and offering first names would produce tokens that resolve
                // ambiguously or not at all. Kind 5 keeps its own `Name || Title` mapping untouched.
                this.collectionOptions[col.Binding] = (opts || [])
                    .map(o => o.Title || o.Name)
                    .filter(n => !!n);
                this.collectionFeedState[col.Binding] = 'ready';
            },
            error: () => {
                this.collectionOptions[col.Binding] = [];
                this.collectionFeedState[col.Binding] = 'failed';
            }
        });
    }

    get pickerLoading(): boolean {
        return !!this.picker && this.collectionFeedState[this.picker.col.Binding] === 'loading';
    }

    get pickerFreeTextOnly(): boolean {
        return !!this.picker && this.collectionFeedState[this.picker.col.Binding] === 'failed';
    }

    /** Client-side contains match over the (capped) feed, display-capped so the cell stays usable. */
    get pickerSuggestions(): string[] {
        if (!this.picker) return [];
        const all = this.collectionOptions[this.picker.col.Binding] || [];
        const q = (this.picker.text || '').trim().toLocaleLowerCase();
        const hit = this.pickableOnly(q ? all.filter(o => o.toLocaleLowerCase().indexOf(q) >= 0) : all);
        return hit.slice(0, 30);
    }

    get pickerTruncated(): boolean {
        if (!this.picker) return false;
        const all = this.collectionOptions[this.picker.col.Binding] || [];
        const q = (this.picker.text || '').trim().toLocaleLowerCase();
        const hit = this.pickableOnly(q ? all.filter(o => o.toLocaleLowerCase().indexOf(q) >= 0) : all);
        return hit.length > 30;
    }

    /**
     * A registry NAME that itself contains a splitter cannot survive a delimited cell: this database
     * really does hold materials called "akril, platno" and "platno, lesonit", and picking one would
     * insert a token that tokensOf() splits straight back into two wrong ones. Offering an option
     * that corrupts itself on click is worse than not offering it. Same rule the backend already
     * applies when it picks example values for the template, BulkService.cs:2014-2015.
     */
    private pickableOnly(options: string[]): string[] {
        const splitters = (this.picker && this.picker.col.SplitterChars) ? this.picker.col.SplitterChars : ',;';
        return options.filter(o => {
            for (let i = 0; i < splitters.length; i++) { if (o.indexOf(splitters[i]) >= 0) return false; }
            return true;
        });
    }

    chooseSuggestion(name: string): void {
        if (!this.picker) return;
        this.picker.text = name;
        this.applyPicker();
    }

    messagesFor(rowIndex: number, binding: string): BulkRowMessage[] {
        return this.cellMessages[rowIndex + '|' + binding] || [];
    }

    cellState(rowIndex: number, binding: string): string {
        const msgs = this.messagesFor(rowIndex, binding);
        if (!msgs.length) return '';
        return msgs.some(m => m.IsError) ? 'err' : 'warn';
    }

    rowState(rowIndex: number): string {
        if (!this.validation) return 'ok';
        if (this.rowHasErr[rowIndex]) return 'err';
        if (this.rowHasWarn[rowIndex]) return 'warn';
        return 'ok';
    }

    /** What the server bound a pasted NAME to. Shown next to the cell so the user can see it. */
    resolvedId(rowIndex: number, binding: string): number {
        if (!this.validation || !this.validation.ResolvedLookups) return null;
        const r = this.validation.ResolvedLookups[rowIndex];
        return r && r[binding] != null ? r[binding] : null;
    }

    get errorCount(): number {
        return this.validation ? (this.validation.Messages || []).filter(m => m.IsError).length : 0;
    }

    get warningCount(): number {
        return this.validation ? (this.validation.Messages || []).filter(m => !m.IsError).length : 0;
    }

    /** Rows with at least one error, whole sheet. These can never be selected. */
    get errorRowCount(): number {
        return this.rowHasErr.filter(Boolean).length;
    }

    /** Rows the user COULD tick, whole sheet. */
    get selectableRowCount(): number {
        if (!this.validation) return 0;
        return this.rows.length - this.errorRowCount;
    }

    /** Kept for the old name: rows without a blocking error, whole sheet. */
    get validRowCount(): number {
        return this.selectableRowCount;
    }

    /** What will actually be POSTed. The pills and the commit button both read THIS. */
    get selectedCount(): number {
        let n = 0;
        for (let i = 0; i < this.rows.length; i++) {
            if (this.selected[i] && !this.rowHasErr[i]) n++;
        }
        return n;
    }

    get canCommit(): boolean {
        // Deliberately NOT gated on validation.IsValid. A sheet with 12 bad rows out of 250 must
        // still be able to import the other 238 - that is the whole point of row selection.
        return !!this.validation && !this.busy && this.selectedCount > 0;
    }

    /** Re-validation is required after any edit, so the commit button re-arms only on a fresh pass. */
    onCellEdited(): void {
        this.validation = null;
        this.cellMessages = {};
        this.rowLevelMessages = {};
        this.rowHasErr = this.rows.map(() => false);
        this.rowHasWarn = this.rows.map(() => false);
        this.recomputeVisible();
    }

    // ---------------------------------------------------------------- step 3: column visibility

    /**
     * Default view = the columns the sheet mapped, PLUS any column carrying a validation message.
     * The second half is not cosmetic: `rows` holds every binding and the server validates all of
     * them, so a required column the sheet never mapped can fail. Hiding it would show a red,
     * unselectable row with nothing on screen explaining why.
     *
     * Ticked view = every mappable column. Unsupported (Kind 7) columns are never in `this.columns`
     * and therefore can never appear here — the pipeline cannot write them.
     */
    recomputeGridColumns(): void {
        if (this.showAllColumns || !this.mappedBindings.length) {
            this.gridColumns = this.columns;
            return;
        }
        const show = new Set(this.mappedBindings);
        this.messageBindings.forEach(b => show.add(b));
        this.gridColumns = this.columns.filter(c => show.has(c.Binding));
    }

    setShowAllColumns(on: boolean): void {
        this.showAllColumns = on;
        this.recomputeGridColumns();
    }

    /** Columns rendered only because they carry an error/warning, while the box is unticked. */
    get revealedByMessageCount(): number {
        if (this.showAllColumns || !this.mappedBindings.length) return 0;
        const mapped = new Set(this.mappedBindings);
        let n = 0;
        this.messageBindings.forEach(b => { if (!mapped.has(b)) n++; });
        return n;
    }

    /** How many mappable columns the default view is hiding. */
    get hiddenColumnCount(): number {
        return Math.max(0, this.columns.length - this.gridColumns.length);
    }

    // ---------------------------------------------------------------- step 3: selection

    isSelectable(rowIndex: number): boolean {
        // Before the first validation pass nothing is known to be bad, so everything is selectable.
        return !this.rowHasErr[rowIndex];
    }

    isSelected(rowIndex: number): boolean {
        return !!this.selected[rowIndex] && this.isSelectable(rowIndex);
    }

    toggleRow(rowIndex: number, checked: boolean): void {
        if (!this.isSelectable(rowIndex)) return;
        // Deliberate user intent outranks the auto-restore in indexMessages.
        this.autoUnselected[rowIndex] = false;
        this.selected[rowIndex] = checked;
    }

    /** WHOLE SHEET, not the current page. The header tick is labelled to say so. */
    get allSelectableSelected(): boolean {
        const n = this.selectableRowCount;
        return n > 0 && this.selectedCount === n;
    }

    toggleAll(checked: boolean): void {
        for (let i = 0; i < this.rows.length; i++) {
            this.autoUnselected[i] = false;
            this.selected[i] = checked && this.isSelectable(i);
        }
    }

    // ---------------------------------------------------------------- step 3: filter + paging

    setErrorsOnly(on: boolean): void {
        this.errorsOnly = on;
        this.pageIndex = 0;
        this.recomputeVisible();
    }

    recomputeVisible(): void {
        const all: number[] = [];
        for (let i = 0; i < this.rows.length; i++) {
            if (!this.errorsOnly || this.rowHasErr[i] || this.rowHasWarn[i]) all.push(i);
        }
        this.filteredIndices = all;

        const pages = Math.max(1, Math.ceil(all.length / this.pageSize));
        if (this.pageIndex > pages - 1) this.pageIndex = pages - 1;
        if (this.pageIndex < 0) this.pageIndex = 0;

        const from = this.pageIndex * this.pageSize;
        this.visibleIndices = all.slice(from, from + this.pageSize);
    }

    get pageCount(): number {
        return Math.max(1, Math.ceil(this.filteredIndices.length / this.pageSize));
    }

    get pageFrom(): number {
        return this.filteredIndices.length === 0 ? 0 : this.pageIndex * this.pageSize + 1;
    }

    get pageTo(): number {
        return Math.min((this.pageIndex + 1) * this.pageSize, this.filteredIndices.length);
    }

    goToPage(i: number): void {
        this.pageIndex = i;
        this.recomputeVisible();
    }

    prevPage(): void { if (this.pageIndex > 0) this.goToPage(this.pageIndex - 1); }
    nextPage(): void { if (this.pageIndex < this.pageCount - 1) this.goToPage(this.pageIndex + 1); }

    onPageSizeChanged(size: any): void {
        const n = parseInt(size, 10);
        this.pageSize = isNaN(n) || n <= 0 ? 50 : n;
        this.pageIndex = 0;
        this.recomputeVisible();
    }

    // ---------------------------------------------------------------- step 4: commit

    commit(): void {
        if (!this.canCommit) return;

        // Build the payload from the ORIGINAL indices and remember them. The server numbers its
        // results against what it was sent, so without this map the step-4 report would name row 3
        // when it meant row 137.
        const idx: number[] = [];
        for (let i = 0; i < this.rows.length; i++) {
            if (this.selected[i] && !this.rowHasErr[i]) idx.push(i);
        }
        if (!idx.length) return;
        this.committedIndices = idx;

        const parsedRows = this.parsed && this.parsed.SheetRowNumbers ? this.parsed.SheetRowNumbers : null;
        const sheetRows = parsedRows && parsedRows.length === this.rows.length
            ? idx.map(i => parsedRows[i])
            : null;

        this.busy = true;
        this.error = null;

        this.http.post<BulkCommitResponse>(
            this.url('/api/bulk/commit/add'),
            {
                ModelFullName: this.modelFullName,
                Rows: idx.map(i => this.rows[i]),
                // PETZ cl. 155/3 - the import has to be recorded as an act, which means naming
                // what was imported. The commit sends JSON rows rather than the workbook, so the
                // server cannot re-derive any of this; it comes from the parse response.
                //
                // ImportId is whatever the LAST parse returned, which is the parse that produced
                // the rows being committed - step 2 re-parses with the mapping applied. That is
                // the correct one to send.
                SourceFileName: this.parsed ? this.parsed.SourceFileName : null,
                ImportId: this.parsed ? this.parsed.ImportId : null,
                // The physical sheet rows behind the committed subset, as the SERVER reported
                // them at parse. Deriving them here from the grid index would be wrong: Rows is
                // compacted server-side, so index+1 names the header row for the first record of
                // every import and drifts further with every blank row. Omitted entirely rather
                // than guessed when parse did not report them - the server records no sheet row
                // instead of a wrong one.
                SourceRowNumbers: sheetRows
            },
            { headers: this.headers() }
        ).subscribe({
            next: (r) => {
                this.commitResult = r;
                this.busy = false;
                this.step = 4;
            },
            error: (e) => this.fail('Uvoz ni uspel', e)
        });
    }

    // ---------------------------------------------------------------- misc

    /**
     * The document layer raises plain English exceptions; this is a Slovene wizard end to end.
     * Matched case-insensitively and by `contains` (not equality) since the product may
     * prefix/suffix the known text. Anything unrecognised falls back to the original string
     * rather than disappearing.
     */
    translateCommitError(raw: string): string {
        if (!raw) { return raw; }
        const lower = raw.toLowerCase();
        if (lower.indexOf('entry with this inventory number already exists') !== -1) {
            return 'Inventarna številka že obstaja.';
        }
        if (lower.indexOf('inventory number is missing') !== -1) {
            return 'Inventarna številka je obvezna.';
        }
        return raw;
    }

    kindLabel(kind: number): string {
        switch (kind) {
            case 1: return 'ŠT';
            case 2: return 'DEC';
            case 3: return 'DATUM';
            case 4: return 'DA/NE';
            case 5: return 'ŠIFRANT';
            case 6: return 'VEČ VREDNOSTI';
            case 7: return 'NI PODPRTO';
            default: return 'BESEDILO';
        }
    }

    kindLabelForBinding(binding: string): string {
        const c = this.columns.find(x => x.Binding === binding);
        return c ? this.kindLabel(c.Kind) : '';
    }

    onCheckToggle(row: { [k: string]: string }, binding: string, checked: boolean): void {
        row[binding] = checked ? 'true' : 'false';
        this.onCellEdited();
    }

    /** Registry columns that are NOT document references get a real dropdown. */
    hasDropdown(col: BulkColumnDef): boolean {
        return col.Kind === 5 && !col.IsDocumentReference;
    }

    optionsFor(col: BulkColumnDef): string[] {
        return this.registryOptions[col.Binding] || [];
    }

    /** Subset-local RowIndex from the server -> the row number the user saw in the grid. */
    originalRowNumber(resultRowIndex: number): number {
        const i = this.committedIndices[resultRowIndex];
        return (i == null ? resultRowIndex : i) + 1;
    }

    restart(): void {
        this.step = 1;
        this.selected = [];
        this.autoUnselected = [];
        this.rowHasErr = [];
        this.rowHasWarn = [];
        this.filteredIndices = [];
        this.visibleIndices = [];
        this.committedIndices = [];
        this.errorsOnly = false;
        this.pageIndex = 0;
        this.file = null;
        this.fileName = null;
        this.parsed = null;
        this.rows = [];
        this.validation = null;
        this.commitResult = null;
        this.cellMessages = {};
        this.rowLevelMessages = {};
        this.mappedBindings = [];
        this.messageBindings = new Set<string>();
        this.showAllColumns = false;
        this.gridColumns = [];
        this.picker = null;
        this.collectionOptions = {};
        this.collectionFeedState = {};
        this.error = null;
    }

    back(to: number): void {
        this.error = null;
        this.step = to;
    }

    trackByIndex(i: number): number { return i; }
}
